import { useEffect, useMemo, useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtDay, fmtMoney } from "../engine/dates";
import { inviteUser, loadAuditLog, loadLastSeen, loadPersonActivity, loadProfiles, loadSignIns, setProfileRole, resetPassword, undoChange, loadXeroStatus, xeroConnectUrl, xeroMerge, anchorToBank } from "../lib/data";

/**
 * Admin: who can do what, and what has been done.
 *
 * The audit trail is written by database triggers, not by this application, so
 * it also catches changes made by a script or straight against the database. An
 * audit log the code has to remember to call is one that a forgotten call
 * quietly puts a hole in.
 */

const ROLES = [
  { id: "admin", label: "Admin", blurb: "Everything, plus this section" },
  { id: "user", label: "User", blurb: "Everything except users and the audit trail" },
  { id: "viewer", label: "Viewer", blurb: "Can see it all, can change nothing" },
];

// Fields that are noise in a trail: timestamps the database sets itself.
const NOISE = new Set(["updated_at", "created_at", "synced_at", "updated_by", "created_by"]);

/** What actually changed, in words rather than two blobs of JSON. */
/**
 * What can be put back from here.
 *
 * The server enforces this list — this copy exists only so the button does not
 * appear for something that would be refused on the way. Accounts are absent
 * on purpose: an account is a profile row plus an auth user, and restoring one
 * half leaves someone who either cannot sign in or signs in to nothing.
 */
const REVERSIBLE = new Set([
  "amex_charges",
  "amex_paydown_overrides",
  "amex_payments",
  "amex_settings",
  "app_settings",
  "doc_dismissals",
  "doc_instalments",
  "doc_paid_marks",
  "doc_spend",
  "doc_updates",
  "expected_payment_dates",
  "investor_comments",
  "manual_entries",
  "on_account_payments",
  "payment_decisions",
  "quote_forecast_overrides",
  "quote_forecast_rules",
  "recurring_costs",
  "recurring_overrides",
  "retainer_settings",
  "scenario_shares",
]);

function describe(row) {
  const { action, before, after } = row;
  if (action === "insert") return summarise(after, "added");
  if (action === "delete") return summarise(before, "removed");
  const changed = [];
  for (const k of Object.keys({ ...(before || {}), ...(after || {}) })) {
    if (NOISE.has(k)) continue;
    const a = before?.[k];
    const b = after?.[k];
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    changed.push(`${k.replace(/_/g, " ")}: ${short(a)} → ${short(b)}`);
  }
  return changed.length ? changed.join(" · ") : "no visible change";
}

function summarise(row, verb) {
  if (!row) return verb;
  const name = row.payee || row.label || row.name || row.description || row.supplier_name;
  const amount = row.amount ?? row.sum ?? row.opening_balance;
  return [verb, name, amount != null ? fmtMoney(Number(amount)) : null].filter(Boolean).join(" · ");
}

/**
 * One person's history: when they signed in, and everything they changed.
 *
 * Deliberately what they *did*, not what they looked at. A record of changes
 * answers the questions worth asking — who moved that date, who switched that
 * retainer off — while a record of clicks mostly produces volume.
 */
function PersonActivity({ person, activity, lastSeen, onClose }) {
  const cell = { padding: "7px 10px", fontSize: 12, verticalAlign: "top" };
  const changes = activity?.changes || [];
  const signIns = activity?.signIns || [];

  return (
    <div
      style={{
        background: T.panel,
        border: `1px solid ${T.accent}`,
        borderRadius: 10,
        padding: 16,
        marginTop: 14,
        maxWidth: 940,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <div style={{ fontFamily: fontDisplay, fontSize: 18 }}>{person.email}</div>
        <div style={{ fontSize: 11, color: T.muted }}>
          {person.role} · {signIns.length} sign-in{signIns.length === 1 ? "" : "s"} recorded ·{" "}
          {changes.length} change{changes.length === 1 ? "" : "s"}
          {lastSeen?.last_sign_in_at ? ` · last seen ${fmtDay(lastSeen.last_sign_in_at)}` : ""}
        </div>
        <button
          onClick={onClose}
          style={{
            marginLeft: "auto",
            border: "none",
            background: "none",
            color: T.muted,
            fontSize: 18,
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {!activity && <div style={{ fontSize: 12, color: T.muted, marginTop: 10 }}>Loading…</div>}

      {activity && (
        <div style={{ display: "flex", gap: 20, marginTop: 12, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 300px", minWidth: 0 }}>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 4 }}>
              Changes they made
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {changes.map((r) => (
                    <tr key={r.id} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
                      <td style={{ ...cell, color: T.muted, fontSize: 10, whiteSpace: "nowrap" }}>
                        {fmtDay(r.at)}
                        <br />
                        {new Date(r.at).toLocaleTimeString("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td style={{ ...cell, fontSize: 11, lineHeight: 1.5 }}>
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color:
                              r.action === "delete"
                                ? T.cost
                                : r.action === "insert"
                                  ? T.income
                                  : T.muted,
                            marginRight: 5,
                          }}
                        >
                          {r.action}
                        </span>
                        {r.entity.replace(/_/g, " ")}
                        <div style={{ color: T.muted, marginTop: 2 }}>{describe(r)}</div>
                      </td>
                    </tr>
                  ))}
                  {!changes.length && (
                    <tr>
                      <td style={{ ...cell, color: T.muted }}>
                        Nothing changed by this account.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ flex: "0 1 260px", minWidth: 0 }}>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginBottom: 4 }}>
              Sign-ins
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {signIns.map((r) => (
                <div
                  key={r.id}
                  style={{ borderTop: `1px solid ${T.borderSoft}`, padding: "6px 0", fontSize: 11 }}
                >
                  {fmtDay(r.at)}{" "}
                  {new Date(r.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  <div style={{ color: T.muted, fontSize: 10 }}>{describeAgent(r.user_agent)}</div>
                </div>
              ))}
              {!signIns.length && (
                <div style={{ fontSize: 11, color: T.muted, paddingTop: 6 }}>
                  None since this started being recorded.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** The readable half of a user-agent string. */
function describeAgent(ua) {
  if (!ua) return "—";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
      ? "Chrome"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Safari\//.test(ua)
          ? "Safari"
          : "a browser";
  const os = /iPhone|iPad/.test(ua)
    ? "iPhone"
    : /Android/.test(ua)
      ? "Android"
      : /Mac OS X/.test(ua)
        ? "Mac"
        : /Windows/.test(ua)
          ? "Windows"
          : "";
  return [browser, os].filter(Boolean).join(" on ");
}

function short(v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return String(Math.round(v * 100) / 100);
  const s = String(v);
  return s.length > 40 ? `${s.slice(0, 39)}…` : s;
}

/**
 * The credentials, shown once.
 *
 * Once because that is the only honest option: the password is not stored
 * anywhere it could be shown again, only its hash is. Laid out as one block to
 * copy, since the alternative is somebody transcribing a password by hand into
 * a message and getting it wrong.
 */
function Credentials({ res, onClose }) {
  const [copied, setCopied] = useState(false);
  const block = [
    `Cash flow forecast — your login`,
    ``,
    `Address:  ${res.url}`,
    `Username: ${res.email}`,
    `Password: ${res.password}`,
    ``,
    res.mustChange === false
      ? `This password will keep working until you change it.`
      : `You will be asked to choose your own password the first time you sign in.`,
  ].join("\n");

  return (
    <div
      style={{
        marginTop: 12,
        border: `1px solid ${T.income}`,
        background: "#EDF3ED",
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: T.income, marginBottom: 6 }}>
        {res.reset || res.existed ? "Password reset" : "Account created"} · {res.role}
        {res.mustChange === false ? " · they keep this password" : ""}
      </div>
      <pre
        style={{
          fontFamily: fontMono,
          fontSize: 11,
          lineHeight: 1.6,
          margin: 0,
          whiteSpace: "pre-wrap",
          userSelect: "all",
        }}
      >
        {block}
      </pre>
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
        <button
          onClick={() => {
            navigator.clipboard?.writeText(block);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{
            padding: "6px 12px",
            borderRadius: 6,
            border: "none",
            background: T.accent,
            color: "#fff",
            fontSize: 11,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {copied ? "copied" : "copy"}
        </button>
        <button
          onClick={onClose}
          style={{
            border: "none",
            background: "none",
            color: T.muted,
            fontSize: 11,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          done
        </button>
        <span style={{ fontSize: 10, color: T.muted, marginLeft: "auto" }}>
          shown once — it cannot be retrieved later
        </span>
      </div>
    </div>
  );
}

export default function AdminView({ me, openingBalance, openingDate, overdraft, fxRates = {}, excluded, onSaveOpening, openingError, onChanged, derivedBalance }) {
  const [profiles, setProfiles] = useState([]);
  const [log, setLog] = useState([]);
  const [undoing, setUndoing] = useState(null);
  const [undoNote, setUndoNote] = useState(null);
  const [xero, setXero] = useState(null);
  const [anchored, setAnchored] = useState(null);
  const readOnlyBank = false;
  const [xeroBusy, setXeroBusy] = useState(false);
  const [mergePlan, setMergePlan] = useState(null);
  const [signIns, setSignIns] = useState([]);
  const [lastSeen, setLastSeen] = useState([]);
  const [person, setPerson] = useState(null);
  const [activity, setActivity] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [invite, setInvite] = useState({ email: "", role: "viewer" });
  const [invited, setInvited] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [forceChange, setForceChange] = useState(true);

  const refresh = async () => {
    try {
      const [p, l, s2, seen] = await Promise.all([
        loadProfiles(),
        loadAuditLog({ limit: 300 }),
        loadSignIns(200),
        loadLastSeen().catch(() => []),
      ]);
      setProfiles(p);
      setLog(l);
      setSignIns(s2);
      setLastSeen(seen);
    } catch (e) {
      setError(e.message || String(e));
    }
  };
  /**
   * Undo one change, then reload twice over: the trail, so it shows the undo
   * itself, and the forecast, because a figure on every other tab has just
   * moved and a stale grid is worse than no grid.
   */
  const runUndo = async (row, force) => {
    setUndoing(row.id);
    setUndoNote(null);
    try {
      const res = await undoChange(row.id, { force });
      setUndoNote({ id: row.id, text: `Done — ${res.did}.` });
      await refresh();
      await onChanged?.();
    } catch (e) {
      setUndoNote({ id: row.id, text: e.message, error: true, conflict: e.conflict });
    } finally {
      setUndoing(null);
    }
  };

  useEffect(() => {
    loadXeroStatus()
      .then(setXero)
      .catch(() => setXero({ connected: false, unavailable: true }));
  }, []);

  useEffect(() => {
    refresh();
  }, []);

  const entities = useMemo(() => [...new Set(log.map((r) => r.entity))].sort(), [log]);
  const shown = useMemo(
    () => (filter ? log.filter((r) => r.entity === filter) : log),
    [log, filter]
  );

  const cell = { padding: "8px 10px", fontSize: 12, verticalAlign: "top" };
  const th = { ...cell, textAlign: "left", fontSize: 11, color: T.muted, fontWeight: 500 };

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Admin</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 760 }}>
        Who can do what, the settings the whole forecast is built on, and a record of everything
        that has changed.
      </p>

      {error && <div style={{ color: T.cost, fontSize: 12, margin: "8px 0" }}>{error}</div>}

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "20px 0 6px" }}>
        The opening position
      </h3>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 760, marginTop: 0 }}>
        The one number in the tool with no document behind it. Everything else is measured from
        here, so it belongs where only an admin can reach it.
      </p>
      <div style={{ background: T.panel, borderRadius: 10, padding: 16, maxWidth: 760 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ fontSize: 11, color: T.muted }}>
            Balance
            <br />
            <input
              value={openingBalance}
              onChange={(e) => onSaveOpening({ balance: e.target.value })}
              placeholder="-47074"
              style={{ fontFamily: fontMono, fontSize: 13, padding: "6px 8px", width: 130, marginTop: 2 }}
            />
          </label>
          <label style={{ fontSize: 11, color: T.muted }}>
            Overdraft
            <br />
            <input
              value={overdraft}
              onChange={(e) => onSaveOpening({ overdraft: e.target.value })}
              placeholder="50000"
              style={{ fontFamily: fontMono, fontSize: 13, padding: "6px 8px", width: 130, marginTop: 2 }}
            />
          </label>
          <label style={{ fontSize: 11, color: T.muted }}>
            as at
            <br />
            <input
              type="date"
              value={openingDate}
              onChange={(e) => onSaveOpening({ date: e.target.value })}
              style={{ fontFamily: fontMono, fontSize: 13, padding: "6px 8px", marginTop: 2 }}
            />
          </label>
        </div>
        <div style={{ fontSize: 11, color: openingError ? T.cost : T.muted, marginTop: 10, lineHeight: 1.5 }}>
          {openingError
            ? `Not saved: ${openingError}`
            : "Shared with everyone using the tool, including the investor link and any scenario, so the forecast starts from the same cash position wherever it is opened."}
        </div>
      </div>

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "24px 0 6px" }}>
        Money in other currencies
      </h3>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 760, marginTop: 0 }}>
        A rate per currency, applied to anything Scoro holds in it. Without one those documents
        stay out of the forecast — visibly, on the cash flow tab, rather than silently.
      </p>
      <div style={{ background: T.panel, borderRadius: 10, padding: 16, maxWidth: 760 }}>
        {Object.keys({ ...(excluded?.currencies || {}), ...fxRates }).length === 0 ? (
          <div style={{ fontSize: 12, color: T.muted }}>
            Everything in the system is in sterling.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {Object.keys({ ...(excluded?.currencies || {}), ...fxRates })
              .sort()
              .map((ccy) => (
                <label key={ccy} style={{ fontSize: 11, color: T.muted }}>
                  1 {ccy} buys
                  <br />
                  <input
                    key={`fx-${ccy}-${fxRates[ccy] ?? ""}`}
                    defaultValue={fxRates[ccy] ?? ""}
                    placeholder="0.79"
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      const next = { ...fxRates };
                      if (v === "") delete next[ccy];
                      else next[ccy] = Number(v);
                      if (JSON.stringify(next) !== JSON.stringify(fxRates)) {
                        onSaveOpening({ fxRates: next });
                      }
                    }}
                    style={{
                      fontFamily: fontMono,
                      fontSize: 13,
                      padding: "6px 8px",
                      width: 100,
                      marginTop: 2,
                    }}
                  />
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>
                    pounds
                    {excluded?.currencies?.[ccy]
                      ? ` · ${Math.round(excluded.currencies[ccy]).toLocaleString()} ${ccy} waiting`
                      : ""}
                  </div>
                </label>
              ))}
          </div>
        )}
        <div style={{ fontSize: 11, color: T.muted, marginTop: 12, lineHeight: 1.5 }}>
          A rate typed here is a judgement, like the opening balance — it does not track the market,
          and the forecast will keep using it until someone changes it. Documents converted with it
          say so when opened.
        </div>
      </div>

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "24px 0 6px" }}>People</h3>
      <div style={{ background: T.panel, borderRadius: 10, padding: 14, marginBottom: 10, maxWidth: 760 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ fontSize: 11, color: T.muted }}>
            Add someone
            <br />
            <input
              type="email"
              value={invite.email}
              onChange={(e) => setInvite({ ...invite, email: e.target.value })}
              placeholder="name@madewithsyrup.com"
              style={{ fontSize: 12, padding: "6px 8px", width: 250, marginTop: 2 }}
            />
          </label>
          <label style={{ fontSize: 11, color: T.muted }}>
            as
            <br />
            <select
              value={invite.role}
              onChange={(e) => setInvite({ ...invite, role: e.target.value })}
              style={{ fontFamily: "inherit", fontSize: 12, padding: "6px 8px", marginTop: 2 }}
            >
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <button
            disabled={busy === "invite" || !invite.email.trim()}
            onClick={async () => {
              setBusy("invite");
              setError(null);
              setInvited(null);
              try {
                const res = await inviteUser(invite.email.trim(), invite.role);
                setInvited(res);
                setInvite({ email: "", role: "viewer" });
                await refresh();
              } catch (e) {
                setError(e.message || String(e));
              } finally {
                setBusy(null);
              }
            }}
            style={{
              padding: "7px 14px",
              borderRadius: 6,
              border: "none",
              background: invite.email.trim() ? T.accent : T.border,
              color: invite.email.trim() ? "#fff" : T.muted,
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {busy === "invite" ? "Creating…" : "Create account"}
          </button>
        </div>
        {invited && <Credentials res={invited} onClose={() => setInvited(null)} />}
        <div style={{ fontSize: 10, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
          A password is generated and shown once. It stops working the first time they sign in —
          the app makes them choose their own before it shows them anything.
        </div>
      </div>
      <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto", maxWidth: 760 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Who</th>
              <th style={th}>Can</th>
              <th style={th}>Last signed in</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => (
              <tr key={p.id} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
                <td style={cell}>
                  <button
                    onClick={async () => {
                      setPerson(p);
                      setActivity(null);
                      try {
                        setActivity(await loadPersonActivity(p.email));
                      } catch (e) {
                        setError(e.message || String(e));
                      }
                    }}
                    style={{
                      border: "none",
                      background: "none",
                      font: "inherit",
                      color: T.accent,
                      cursor: "pointer",
                      padding: 0,
                      textAlign: "left",
                      textDecoration: "underline",
                    }}
                  >
                    {p.email}
                  </button>
                  {p.id === me?.id && (
                    <span style={{ fontSize: 10, color: T.muted, marginLeft: 6 }}>you</span>
                  )}
                </td>
                <td style={cell}>
                  <select
                    value={p.role}
                    disabled={busy === p.id || p.id === me?.id}
                    title={p.id === me?.id ? "You cannot change your own access" : undefined}
                    onChange={async (e) => {
                      setBusy(p.id);
                      setError(null);
                      try {
                        await setProfileRole(p.id, e.target.value);
                        await refresh();
                      } catch (err) {
                        setError(err.message || String(err));
                      } finally {
                        setBusy(null);
                      }
                    }}
                    style={{ fontFamily: "inherit", fontSize: 12, padding: "4px 6px" }}
                  >
                    {ROLES.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>
                    {ROLES.find((r) => r.id === p.role)?.blurb}
                  </div>
                  {/* Two clicks, because it takes effect immediately and locks
                      the person out of whatever they knew. */}
                  {resetting === p.id ? (
                    <div style={{ marginTop: 6, fontSize: 11 }}>
                      <label style={{ display: "flex", gap: 5, alignItems: "center", color: T.muted }}>
                        <input
                          type="checkbox"
                          checked={forceChange}
                          onChange={(e) => setForceChange(e.target.checked)}
                        />
                        make them choose their own on first use
                      </label>
                      <div style={{ display: "flex", gap: 8, marginTop: 5 }}>
                        <button
                          disabled={busy === p.id}
                          onClick={async () => {
                            setBusy(p.id);
                            setError(null);
                            try {
                              setInvited(await resetPassword(p.id, { mustChange: forceChange }));
                              setResetting(null);
                              await refresh();
                            } catch (err) {
                              setError(err.message || String(err));
                            } finally {
                              setBusy(null);
                            }
                          }}
                          style={{
                            padding: "3px 10px",
                            borderRadius: 5,
                            border: "none",
                            background: T.cost,
                            color: "#fff",
                            fontSize: 11,
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                        >
                          {busy === p.id ? "Resetting…" : "Reset it now"}
                        </button>
                        <button
                          onClick={() => setResetting(null)}
                          style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
                        >
                          cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setForceChange(true);
                        setResetting(p.id);
                      }}
                      title="Give this person a new password. The old one stops working immediately."
                      style={{
                        marginTop: 5,
                        border: "none",
                        background: "none",
                        color: T.muted,
                        fontSize: 10,
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontFamily: "inherit",
                        padding: 0,
                      }}
                    >
                      reset password
                    </button>
                  )}
                </td>
                <td style={{ ...cell, color: T.muted, fontSize: 11 }}>
                  {(() => {
                    const seen = lastSeen.find((x) => x.email === p.email)?.last_sign_in_at;
                    return seen ? (
                      <>
                        {fmtDay(seen)}{" "}
                        {new Date(seen).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </>
                    ) : (
                      <span style={{ color: T.warn }}>never signed in</span>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 8, lineHeight: 1.6, maxWidth: 760 }}>
        Accounts are created by signing up, and arrive as <strong>Viewer</strong> until an admin
        says otherwise — a new account should not be able to change the forecast because somebody
        knew the address. You cannot change your own access, so an admin cannot lock themselves out
        by accident.
      </div>

      {person && (
        <PersonActivity
          person={person}
          activity={activity}
          lastSeen={lastSeen.find((x) => x.email === person.email)}
          onClose={() => {
            setPerson(null);
            setActivity(null);
          }}
        />
      )}

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "24px 0 6px" }}>Signing in</h3>
      <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto", maxWidth: 760 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 170 }}>When</th>
              <th style={th}>Who</th>
              <th style={th}>From</th>
            </tr>
          </thead>
          <tbody>
            {signIns.map((r) => (
              <tr key={r.id} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
                <td style={{ ...cell, color: T.muted, fontSize: 11, whiteSpace: "nowrap" }}>
                  {fmtDay(r.at)}{" "}
                  {new Date(r.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </td>
                <td style={{ ...cell, fontSize: 11 }}>{r.email}</td>
                <td style={{ ...cell, fontSize: 11, color: T.muted }}>{describeAgent(r.user_agent)}</td>
              </tr>
            ))}
            {!signIns.length && (
              <tr>
                <td style={{ ...cell, color: T.muted }} colSpan={3}>
                  Nothing recorded yet. This starts from now — there was no history to import.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 8, lineHeight: 1.6, maxWidth: 760 }}>
        Recorded by the app on each sign-in. A row can only be written about yourself and none can
        be edited or removed, so nobody can put a line here about anyone else. No IP address is
        kept — the browser does not reliably know its own, and asking a third party for it would
        mean telling a third party who is signing in.
      </div>

      {/* Money that should be in the forecast and is not.
          A cost whose quote has vanished from Scoro cannot be judged by the
          engine — won work and lost work look identical once the document is
          gone — so it is surfaced rather than quietly dropped. That is how
          £58,000 of supplier spend went missing without anybody noticing. */}
      {!!excluded?.orphanedSpend?.length && (
        <>
          <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "24px 0 6px" }}>
            Costs that have lost their quote
          </h3>
          <div
            style={{
              background: "#FBEFE2",
              border: `1px solid ${T.warn}`,
              borderRadius: 10,
              padding: 14,
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 12, lineHeight: 1.6, maxWidth: 720 }}>
              These costs were attached to quotes that are no longer in Scoro, so there is no way to
              tell whether the work was won or lost. They are <strong>not</strong> in the forecast.
              Re-attach each one to the invoice the work became, or delete it if the job never
              happened.
            </div>
            <div style={{ marginTop: 8 }}>
              {excluded.orphanedSpend.map((o) => (
                <div key={o.id} style={{ fontSize: 12, marginTop: 4 }}>
                  <strong style={{ fontFamily: fontMono }}>{fmtMoney(-o.amount)}</strong> · {o.label}
                  <span style={{ color: T.muted }}> · was on quote {o.quoteId}</span>
                </div>
              ))}
              <div style={{ fontSize: 12, marginTop: 8, fontWeight: 700 }}>
                {fmtMoney(-excluded.orphanedSpend.reduce((t, o) => t + o.amount, 0))} missing in
                total
              </div>
            </div>
          </div>
        </>
      )}

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "24px 0 6px" }}>Xero</h3>
      <div
        style={{
          background: T.panel,
          borderRadius: 10,
          padding: 14,
          marginBottom: 8,
          border: `1px solid ${xero?.connected ? T.borderSoft : T.accent}`,
        }}
      >
        {!xero && <div style={{ fontSize: 12, color: T.muted }}>Checking…</div>}

        {xero && !xero.connected && (
          <>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Not connected yet.</div>
            <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.6, maxWidth: 640 }}>
              Authorising takes one click and happens once. Xero issues a new key every time the
              old one is used, and the sync runs hourly, so the connection keeps itself alive —
              only a two-month outage would need doing again. Read-only: this cannot create, change
              or delete anything in Xero.
            </div>
            <button
              disabled={xeroBusy}
              onClick={async () => {
                setXeroBusy(true);
                try {
                  window.location.href = await xeroConnectUrl();
                } catch (e) {
                  setError(e.message);
                  setXeroBusy(false);
                }
              }}
              style={{
                marginTop: 10,
                padding: "8px 16px",
                borderRadius: 7,
                border: "none",
                background: T.accent,
                color: "#fff",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Connect Xero
            </button>
          </>
        )}

        {xero?.connected && (
          <>
            <div style={{ fontSize: 13 }}>
              Connected to <strong>{xero.tenantName}</strong>
              <span style={{ color: T.muted, fontSize: 11 }}>
                {" "}
                · {xero.billCount} bills mirrored
              </span>
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
              Authorised {xero.connectedAt ? fmtDay(xero.connectedAt) : "—"}
              {xero.connectedBy ? ` by ${xero.connectedBy}` : ""} · key last renewed{" "}
              {xero.lastRefreshAt ? fmtDay(xero.lastRefreshAt) : "never"}
              {" · "}
              <button
                onClick={async () => {
                  try {
                    window.location.href = await xeroConnectUrl();
                  } catch (e) {
                    setError(e.message);
                  }
                }}
                style={{ border: "none", background: "none", color: T.accent, fontSize: 11, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", padding: 0 }}
              >
                reconnect
              </button>
            </div>
            {/* The one figure in this system that is not a claim about money
                but the money itself. Admin only, by database policy as well as
                by what is rendered here — a viewer's session cannot read the
                table at all, so hiding it in the interface is the second line
                of defence rather than the only one. */}
            {xero.bank && (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: T.panelSoft,
                  border: `1px solid ${T.border}`,
                }}
              >
                <div style={{ fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", color: T.muted }}>
                  In the bank · admin only
                </div>
                <div
                  style={{
                    fontFamily: fontDisplay,
                    fontSize: 26,
                    color: xero.bank.balance < 0 ? T.cost : T.income,
                  }}
                >
                  {fmtMoney(xero.bank.balance)}
                </div>
                <div style={{ fontSize: 11, color: T.muted }}>
                  Xero bank summary as at {fmtDay(xero.bank.asAt)}
                </div>
                {/* The anchor moves on its own each night. This is for the
                    moment somebody is looking at a bank statement and wants the
                    forecast to start from it now. */}
                {!readOnlyBank && (
                  <button
                    disabled={xeroBusy}
                    onClick={async () => {
                      setXeroBusy(true);
                      setError(null);
                      try {
                        const r = await anchorToBank();
                        setXero(await loadXeroStatus());
                        await onChanged?.();
                        setError(null);
                        setAnchored(`Forecast now starts from ${fmtMoney(r.balance)} as at ${fmtDay(r.asAt)}.`);
                      } catch (e) {
                        setError(e.message);
                      } finally {
                        setXeroBusy(false);
                      }
                    }}
                    style={{
                      marginTop: 8,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: `1px solid ${T.border}`,
                      background: T.panel,
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Start the forecast from this balance
                  </button>
                )}
                {anchored && (
                  <div style={{ fontSize: 11, color: T.income, marginTop: 6 }}>{anchored}</div>
                )}
                {typeof derivedBalance === "number" && (
                  <div style={{ fontSize: 11, marginTop: 6, color: T.muted, lineHeight: 1.6 }}>
                    The forecast derives {fmtMoney(derivedBalance)} from the opening balance.{" "}
                    {Math.abs(xero.bank.balance - derivedBalance) < 1 ? (
                      <span style={{ color: T.income }}>They agree.</span>
                    ) : (
                      <span style={{ color: T.warn }}>
                        {fmtMoney(Math.abs(xero.bank.balance - derivedBalance))} apart — that gap is
                        movement the model has not accounted for, and is worth chasing rather than
                        splitting the difference.
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {xero.lastRun && !xero.lastRun.ok && (
              <div style={{ fontSize: 11, color: T.cost, marginTop: 6 }}>
                Last sync failed: {xero.lastRun.error}
              </div>
            )}

            {/* A permission cannot be added to a token that already exists,
                so switching something on that needs one is inert until this is
                pressed. Saying which permission is missing turns "nothing
                happened" into a single obvious action. */}
            {!!xero.missingScopes?.length && (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: "#FBEFE2",
                  border: `1px solid ${T.warn}`,
                  fontSize: 12,
                }}
              >
                This connection was approved before{" "}
                {xero.missingScopes.includes("accounting.reports.banksummary.read")
                  ? "the bank balance was switched on"
                  : "these permissions were needed"}
                , so it cannot read{" "}
                {xero.missingScopes.map((sc) => sc.replace("accounting.", "")).join(", ")} yet.
                Approving again grants it — same app, nothing to change in Xero.
                <div>
                  <button
                    disabled={xeroBusy}
                    onClick={async () => {
                      setXeroBusy(true);
                      try {
                        window.location.href = await xeroConnectUrl();
                      } catch (e) {
                        setError(e.message);
                        setXeroBusy(false);
                      }
                    }}
                    style={{
                      marginTop: 8,
                      padding: "7px 15px",
                      borderRadius: 7,
                      border: "none",
                      background: T.accent,
                      color: "#fff",
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Approve again
                  </button>
                </div>
              </div>
            )}

            <button
              disabled={xeroBusy}
              onClick={async () => {
                setXeroBusy(true);
                setError(null);
                try {
                  setMergePlan(await xeroMerge({ apply: false }));
                } catch (e) {
                  setError(e.message);
                } finally {
                  setXeroBusy(false);
                }
              }}
              style={{
                marginTop: 10,
                padding: "6px 13px",
                borderRadius: 6,
                border: `1px solid ${T.border}`,
                background: T.panelSoft,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {xeroBusy ? "Working…" : "Check for duplicates"}
            </button>

            {mergePlan && (
              <div style={{ marginTop: 12, fontSize: 12 }}>
                <div style={{ marginBottom: 6 }}>
                  <strong>{mergePlan.wouldMerge?.length || mergePlan.merged?.length || 0}</strong>{" "}
                  hand-entered bills match a Xero bill exactly
                  {mergePlan.totals ? ` · ${fmtMoney(mergePlan.totals.merging || 0)}` : ""}
                </div>
                {(mergePlan.wouldMerge || mergePlan.merged || []).map((m) => (
                  <div key={m.entryId} style={{ color: T.muted, fontSize: 11, marginLeft: 8 }}>
                    {m.label} {fmtMoney(m.amount)} — keeping your date {fmtDay(m.plannedDate)} over
                    Xero&rsquo;s {fmtDay(m.xeroDueDate)}
                  </div>
                ))}

                {!!mergePlan.needsReview?.length && (
                  <div style={{ marginTop: 10 }}>
                    <strong>{mergePlan.needsReview.length}</strong> need a decision
                    {mergePlan.needsReview.map((r) => (
                      <div key={r.entryId} style={{ color: T.warn, fontSize: 11, marginLeft: 8 }}>
                        {r.label} {fmtMoney(r.amount)} — {r.why}
                      </div>
                    ))}
                  </div>
                )}

                {/* The two leftover lists, which is where the expensive
                    duplicates hide: the same debt under two different names. */}
                {!!mergePlan.leftovers?.entries?.length && (
                  <div style={{ marginTop: 10, display: "flex", gap: 20, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700 }}>Typed by hand, no match</div>
                      {mergePlan.leftovers.entries.slice(0, 6).map((g) => (
                        <div key={g.name} style={{ fontSize: 11, color: T.muted }}>
                          {g.name} {fmtMoney(g.total)} ({g.count})
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700 }}>In Xero, unclaimed</div>
                      {mergePlan.leftovers.bills.slice(0, 6).map((g) => (
                        <div key={g.name} style={{ fontSize: 11, color: T.muted }}>
                          {g.name} {fmtMoney(g.total)} ({g.count})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {mergePlan.dryRun && !!mergePlan.wouldMerge?.length && (
                  <button
                    disabled={xeroBusy}
                    onClick={async () => {
                      setXeroBusy(true);
                      try {
                        setMergePlan(await xeroMerge({ apply: true }));
                        await onChanged?.();
                      } catch (e) {
                        setError(e.message);
                      } finally {
                        setXeroBusy(false);
                      }
                    }}
                    style={{
                      marginTop: 10,
                      padding: "6px 13px",
                      borderRadius: 6,
                      border: "none",
                      background: T.accent,
                      color: "#fff",
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Merge these {mergePlan.wouldMerge.length}
                  </button>
                )}
                {!mergePlan.dryRun && (
                  <div style={{ marginTop: 8, color: T.income, fontSize: 12 }}>
                    Merged. Every one of them can be undone from the trail below.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "24px 0 6px" }}>
        What has changed
      </h3>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ fontFamily: "inherit", fontSize: 12, padding: "5px 8px" }}
        >
          <option value="">everything</option>
          {entities.map((e) => (
            <option key={e} value={e}>
              {e.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: T.muted }}>
          {shown.length} of the last {log.length} changes
        </span>
      </div>
      <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 150 }}>When</th>
              <th style={{ ...th, width: 190 }}>Who</th>
              <th style={{ ...th, width: 170 }}>What</th>
              <th style={th}>Change</th>
              <th style={{ ...th, width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
                <td style={{ ...cell, color: T.muted, fontSize: 11, whiteSpace: "nowrap" }}>
                  {fmtDay(r.at)}{" "}
                  {new Date(r.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </td>
                <td style={{ ...cell, fontSize: 11 }}>{r.actor_email || "the system"}</td>
                <td style={{ ...cell, fontSize: 11 }}>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      color:
                        r.action === "delete" ? T.cost : r.action === "insert" ? T.income : T.muted,
                      marginRight: 5,
                    }}
                  >
                    {r.action}
                  </span>
                  {r.entity.replace(/_/g, " ")}
                </td>
                <td style={{ ...cell, fontSize: 11, lineHeight: 1.5 }}>
                  {describe(r)}
                  {undoNote?.id === r.id && (
                    <div
                      style={{
                        fontSize: 11,
                        marginTop: 5,
                        color: undoNote.error ? T.cost : T.income,
                      }}
                    >
                      {undoNote.text}
                      {undoNote.conflict && (
                        <button
                          onClick={() => runUndo(r, true)}
                          style={{
                            marginLeft: 8,
                            fontFamily: "inherit",
                            fontSize: 11,
                            padding: "2px 8px",
                            borderRadius: 5,
                            border: `1px solid ${T.cost}`,
                            background: "transparent",
                            color: T.cost,
                            cursor: "pointer",
                          }}
                        >
                          undo it anyway
                        </button>
                      )}
                    </div>
                  )}
                </td>
                <td style={{ ...cell, textAlign: "right" }}>
                  {REVERSIBLE.has(r.entity) && (
                    <button
                      onClick={() => runUndo(r, false)}
                      disabled={undoing === r.id}
                      title="Put this back as it was"
                      style={{
                        fontFamily: "inherit",
                        fontSize: 11,
                        padding: "3px 9px",
                        borderRadius: 6,
                        border: `1px solid ${T.border}`,
                        background: T.panelSoft,
                        color: T.muted,
                        cursor: undoing === r.id ? "default" : "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {undoing === r.id ? "…" : "undo"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!shown.length && (
              <tr>
                <td style={{ ...cell, color: T.muted }} colSpan={5}>
                  Nothing recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 8, lineHeight: 1.6, maxWidth: 760 }}>
        Written by the database itself rather than by the app, so a change made by a script or
        straight against the database appears here too. The hourly Scoro sync is deliberately not
        logged — it rewrites its mirror every hour, and recording that would bury every real
        decision under thousands of machine writes.
      </div>
    </div>
  );
}
